import { useCallback, useEffect, useState } from "react";

import {
    createPartner,
    deletePartner,
    getPartners,
    updatePartner,
    updatePartnerAccount,
} from "services/partners/rest";
import { PartnerDto } from "services/partners/restModels";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

import { PartnerFormValues } from "../components/PartnerFormDialog";

export type PartnerDialog =
    | { kind: "create" }
    | { kind: "edit"; partner: PartnerDto }
    | { kind: "account"; partner: PartnerDto }
    | { kind: "delete"; partner: PartnerDto };

export const usePartnersState = () => {
    const notify = useNotificationSnackbar();

    const [partners, setPartners] = useState<PartnerDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dialog, setDialog] = useState<PartnerDialog | null>(null);

    const loadPartners = useCallback(async () => {
        setLoading(true);

        const response = await getPartners();

        setPartners(response || []);
        setError(response === null);
        setLoading(false);
    }, []);

    useEffect(() => {
        loadPartners();
    }, [loadPartners]);

    const run = async (action: () => Promise<void>, successTitle: string, failureTitle: string) => {
        setSaving(true);

        try {
            await action();
            notify.showSuccess(successTitle);
            setDialog(null);
        } catch (caught) {
            notify.showError(failureTitle, caught);
        } finally {
            setSaving(false);
        }
    };

    const replacePartner = (updated: PartnerDto) =>
        setPartners((current) =>
            current.map((partner) => (partner.id === updated.id ? updated : partner))
        );

    const handleSubmitForm = (values: PartnerFormValues) => {
        if (dialog?.kind === "edit") {
            const { name, commissionPercent } = values;

            return run(
                async () => replacePartner(await updatePartner(dialog.partner.id, { name, commissionPercent })),
                "Партнёр обновлён",
                "Не удалось сохранить партнёра"
            );
        }

        return run(
            async () => {
                const created = await createPartner({
                    name: values.name,
                    commissionPercent: values.commissionPercent,
                    accountId: values.accountId,
                });

                setPartners((current) => [...current, created]);
            },
            "Партнёр создан",
            "Не удалось создать партнёра"
        );
    };

    const handleSubmitAccount = (accountId: number) => {
        if (dialog?.kind !== "account") return;

        run(
            async () => replacePartner(await updatePartnerAccount(dialog.partner.id, { accountId })),
            "Аккаунт партнёра изменён",
            "Не удалось сменить аккаунт"
        );
    };

    const handleConfirmDelete = () => {
        if (dialog?.kind !== "delete") return;

        const { id } = dialog.partner;

        run(
            async () => {
                await deletePartner(id);
                setPartners((current) => current.filter((partner) => partner.id !== id));
            },
            "Партнёр удалён",
            "Не удалось удалить партнёра"
        );
    };

    return {
        partners,
        loading,
        error,
        saving,
        dialog,
        notify,
        onStartCreating: () => setDialog({ kind: "create" }),
        onStartEditing: (partner: PartnerDto) => setDialog({ kind: "edit", partner }),
        onStartChangingAccount: (partner: PartnerDto) => setDialog({ kind: "account", partner }),
        onStartDeleting: (partner: PartnerDto) => setDialog({ kind: "delete", partner }),
        onCloseDialog: () => setDialog(null),
        onSubmitForm: handleSubmitForm,
        onSubmitAccount: handleSubmitAccount,
        onConfirmDelete: handleConfirmDelete,
        onRetry: loadPartners,
    };
};
